import React, { useEffect, useMemo, useState } from "react"
import { motion } from "framer-motion"
import API from "../Common/API"
import { useHistory } from "react-router-dom"
import DoughnutChart from "../Components/DoughnutChart"
import { Bar } from "react-chartjs-2"
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
} from "chart.js"
import { FaMoneyBillTrendUp, FaUserCheck, FaUserMinus, FaUsers } from "react-icons/fa6"
import { RiPassPendingFill } from "react-icons/ri"
import { ImUserMinus } from "react-icons/im"
import { PiTreeViewDuotone, PiTreeViewFill, PiTreeViewLight } from "react-icons/pi"
import { HiOutlineUserPlus } from "react-icons/hi2"

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const DASHBOARD_CACHE_KEY = "matrimony_dashboard_details"
const EMPTY_DASHBOARD_DETAILS = {
    pendingUser: 0,
    approvedUser: 0,
    paidMembers: 0,
    happyCustomer: 0,
    totalUser: 0,
    totalMale: 0,
    totalFemale: 0,
    updatedUser: 0,
    deactivatedUser: 0,
    declinedUser: 0,
    inactiveUser: 0,
    blockedUser: 0,
    totalPlans: 0,
    activePlans: 0,
    deactivatedPlans: 0,
}

const getCachedDashboardDetails = () => {
    try {
        const cached = sessionStorage.getItem(DASHBOARD_CACHE_KEY)
        return cached ? JSON.parse(cached) : null
    } catch {
        return null
    }
}

const DashboardSkeleton = () => (
    <div className="space-y-8">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((item) => (
                <div key={item} className="h-[170px] animate-pulse rounded-xl border border-borderColor bg-white p-6 shadow-sm">
                    <div className="h-4 w-32 rounded-full bg-slate-200" />
                    <div className="mt-6 h-10 w-24 rounded-full bg-primeColor/10" />
                    <div className="mt-5 h-4 w-44 rounded-full bg-slate-200" />
                </div>
            ))}
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="h-[340px] animate-pulse rounded-xl border border-borderColor bg-white p-6 shadow-sm lg:col-span-2" />
            <div className="h-[340px] animate-pulse rounded-xl border border-borderColor bg-white p-6 shadow-sm" />
        </div>
        <div className="h-[420px] animate-pulse rounded-xl border border-borderColor bg-white p-6 shadow-sm" />
    </div>
)

const startOfToday = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
}

const Dashboard = () => {
    const [dashDetails, setDashDetails] = useState(() => getCachedDashboardDetails())
    const [, setLoading] = useState(() => !getCachedDashboardDetails())
    const [planSubscriptions, setPlanSubscriptions] = useState([])
    const [allUsers, setAllUsers] = useState([])
    const history = useHistory()

    useEffect(() => {
        let active = true
        setLoading(!dashDetails)

        Promise.allSettled([
            API.get("/common/getAllDashboardDetails"),
            API.get("/plans/getAllUserPlan"),
            API.get("/plans/getPlansList"),
            API.get("/user/getUserList", { params: { page: 1, limit: 500, search: "" } }),
        ]).then((responses) => {
            if (!active) return

            const [dashboardRes, subscriptionsRes, , usersRes] = responses

            if (usersRes.status === "fulfilled" && usersRes.value.status === 200) {
                const payload = usersRes.value.data?.data || usersRes.value.data
                const users = Array.isArray(payload) ? payload : (payload?.docs || payload?.users || payload?.data || [])
                setAllUsers(users)
            }

            if (dashboardRes.status === "fulfilled" && dashboardRes.value.status === 200) {
                setDashDetails(dashboardRes.value.data.data)
                sessionStorage.setItem(DASHBOARD_CACHE_KEY, JSON.stringify(dashboardRes.value.data.data))
            } else if (!dashDetails) {
                setDashDetails(EMPTY_DASHBOARD_DETAILS)
            }

            if (subscriptionsRes.status === "fulfilled" && subscriptionsRes.value.status === 200) {
                setPlanSubscriptions(subscriptionsRes.value.data.data || [])
            }

            setLoading(false)
        }).catch(() => {
            if (!active) return
            if (!dashDetails) setDashDetails(EMPTY_DASHBOARD_DETAILS)
            setLoading(false)
        })

        return () => {
            active = false
        }
    }, []) // eslint-disable-line

    const stats = useMemo(() => [
        { id: "pending", label: "Pending Approvals", value: dashDetails?.pendingUser || 0, icon: RiPassPendingFill, color: "#E74C3C", change: "+12%", path: "/user/0" },
        { id: "approved", label: "Approved Users", value: dashDetails?.approvedUser || 0, icon: FaUserCheck, color: "#3498DB", change: "+8%", path: "/user/1" },
        { id: "paid", label: "Paid Members", value: dashDetails?.paidMembers || 0, icon: FaMoneyBillTrendUp, color: "#27AE60", change: "+15%", path: "/user/7" },
        { id: "happy", label: "Happy Customers", value: dashDetails?.happyCustomer || 0, icon: ImUserMinus, color: "#F39C12", change: "+22%", path: "/user/4" },
    ], [dashDetails])

    const secondaryStats = useMemo(() => [
        { id: "total-users",        label: "Total Users",        value: dashDetails?.totalUser        || 0, icon: FaUsers,           color: "#6366F1", bg: "#EEF2FF", path: "/user" },
        { id: "updated",            label: "Updated Users",      value: dashDetails?.updatedUser      || 0, icon: FaUserCheck,       color: "#0EA5E9", bg: "#E0F2FE", path: "/user" },
        { id: "deactivated",        label: "Deactivated Users",  value: dashDetails?.deactivatedUser  || 0, icon: FaUserMinus,       color: "#F97316", bg: "#FFF7ED", path: "/user" },
        { id: "declined",           label: "Declined Users",     value: dashDetails?.declinedUser     || 0, icon: FaUserMinus,       color: "#EF4444", bg: "#FEF2F2", path: "/user/2" },
        { id: "inactive",           label: "Inactive Users",     value: dashDetails?.inactiveUser     || 0, icon: FaUserMinus,       color: "#8B5CF6", bg: "#F5F3FF", path: "/user/3" },
        { id: "blocked",            label: "Blocked Users",      value: dashDetails?.blockedUser      || 0, icon: FaUserMinus,       color: "#DC2626", bg: "#FEE2E2", path: "/user/5" },
        { id: "total-plans",        label: "Total Plans",        value: dashDetails?.totalPlans       || 0, icon: PiTreeViewDuotone, color: "#059669", bg: "#ECFDF5", path: "/plan" },
        { id: "active-plans",       label: "Active Plans",       value: dashDetails?.activePlans      || 0, icon: PiTreeViewFill,    color: "#10B981", bg: "#D1FAE5", path: "/plan" },
        { id: "deactivated-plans",  label: "Deactivated Plans",  value: dashDetails?.deactivatedPlans || 0, icon: PiTreeViewLight,   color: "#6B7280", bg: "#F3F4F6", path: "/plan" },
    ], [dashDetails])

    const recentRegistrations = useMemo(() => {
        const now = Date.now()
        const startToday = new Date(); startToday.setHours(0, 0, 0, 0)
        const startWeek = new Date(now - 7 * 24 * 60 * 60 * 1000)
        const startMonth = new Date(now - 30 * 24 * 60 * 60 * 1000)
        let today = 0, week = 0, month = 0
        allUsers.forEach((u) => {
            const t = u.createdAt ? new Date(u.createdAt).getTime() : 0
            if (!t) return
            if (t >= startToday.getTime()) today++
            if (t >= startWeek.getTime()) week++
            if (t >= startMonth.getTime()) month++
        })
        return { today, week, month }
    }, [allUsers])

    const activeSubscriptions = useMemo(() => {
        const today = startOfToday()
        return planSubscriptions.filter((subscription) => {
            const expiry = subscription?.planValidityDate ? new Date(subscription.planValidityDate) : null
            return expiry && expiry >= today
        })
    }, [planSubscriptions])

    const revenueSummary = useMemo(() => {
        return activeSubscriptions.reduce((sum, subscription) => sum + Number(subscription?.planId?.planRate || 0), 0)
    }, [activeSubscriptions])

    const planWiseReport = useMemo(() => {
        const grouped = activeSubscriptions.reduce((acc, subscription) => {
            const planId = subscription?.planId?._id
            const planName = subscription?.planId?.planName
            if (!planId || !planName) return acc

            if (!acc[planId]) {
                acc[planId] = {
                    planId,
                    planName,
                    users: 0,
                    revenue: 0,
                }
            }

            acc[planId].users += 1
            acc[planId].revenue += Number(subscription?.planId?.planRate || 0)
            return acc
        }, {})

        return Object.values(grouped).sort((a, b) => b.users - a.users)
    }, [activeSubscriptions])

    const expiryHighlights = useMemo(() => {
        const today = startOfToday()
        const endOfWeek = new Date(today)
        endOfWeek.setDate(today.getDate() + 7)

        const todayExpiring = []
        const weekExpiring = []

        activeSubscriptions.forEach((subscription) => {
            const expiry = new Date(subscription.planValidityDate)
            expiry.setHours(0, 0, 0, 0)

            if (expiry.getTime() === today.getTime()) {
                todayExpiring.push(subscription)
            } else if (expiry > today && expiry <= endOfWeek) {
                weekExpiring.push(subscription)
            }
        })

        return { todayExpiring, weekExpiring }
    }, [activeSubscriptions])

    const activityData = useMemo(() => ({
        labels: ["Jan", "Feb", "Mar", "Apr"],
        datasets: [
            {
                label: "Approvals",
                data: [120, 150, 145, 170],
                backgroundColor: "#E74C3C",
                borderRadius: 8,
            },
            {
                label: "Registrations",
                data: [180, 220, 240, 280],
                backgroundColor: "#3498DB",
                borderRadius: 8,
            },
            {
                label: "Marriages",
                data: [45, 58, 62, 71],
                backgroundColor: "#27AE60",
                borderRadius: 8,
            },
        ],
    }), [])

    const activityOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    usePointStyle: true,
                    color: "#5B6773",
                    font: { family: "Roboto", weight: 500 },
                },
            },
            tooltip: {
                backgroundColor: "#FFFFFF",
                titleColor: "#2C3E50",
                bodyColor: "#2C3E50",
                borderColor: "rgba(52, 73, 94, 0.1)",
                borderWidth: 1,
                padding: 12,
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { color: "#5B6773" },
            },
            y: {
                grid: { color: "rgba(52, 73, 94, 0.08)" },
                ticks: { color: "#5B6773" },
            },
        },
    }

    return (
        <motion.div className="space-y-8">
            {!dashDetails ? (
                <DashboardSkeleton />
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                        {stats.map((stat, index) => {
                            const Icon = stat.icon
                            return (
                                <motion.button
                                    key={stat.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.08 }}
                                    className="group rounded-xl p-6 text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
                                    style={{ background: `linear-gradient(135deg, ${stat.color}18 0%, ${stat.color}08 100%)`, border: `1.5px solid ${stat.color}30` }}
                                    onClick={() => history.push(stat.path)}
                                    type="button"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <p className="mb-2 text-sm font-semibold" style={{ color: stat.color }}>{stat.label}</p>
                                            <h3 className="text-3xl font-bold text-tColor">{Number(stat.value).toLocaleString()}</h3>
                                            <div className="mt-3 flex items-center gap-2">
                                                <span className="rounded-full px-2 py-1 text-xs font-bold" style={{ backgroundColor: stat.color + "20", color: stat.color }}>{stat.change}</span>
                                                <span className="text-xs text-slate-500">vs last month</span>
                                            </div>
                                        </div>
                                        <div className="flex h-12 w-12 items-center justify-center rounded-xl transition-transform group-hover:scale-110" style={{ backgroundColor: stat.color }}>
                                            <Icon className="h-6 w-6 text-white" />
                                        </div>
                                    </div>
                                </motion.button>
                            )
                        })}
                    </div>

                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.24 }}
                            className="rounded-xl border border-borderColor bg-white p-6 shadow-sm xl:col-span-12"
                        >
                            <div className="mb-5 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-primeColor">Expiry Focus</p>
                                    <h3 className="mt-1 text-xl font-semibold text-tColor">Plans expiring soon</h3>
                                </div>
                                <button
                                    type="button"
                                    className="w-fit rounded-full border border-borderColor bg-white px-4 py-2 text-xs font-bold text-tColor transition hover:border-primeColor hover:text-primeColor"
                                    onClick={() => history.push("/planValidity")}
                                >
                                    Open Subscription List
                                </button>
                            </div>
                            <div className="grid gap-4 md:grid-cols-2">
                                <button
                                    type="button"
                                    onClick={() => history.push("/planValidity?expiry=today")}
                                    className="flex items-center justify-between rounded-2xl border border-red-100 bg-red-50 px-4 py-4 text-left transition hover:border-red-200 hover:bg-red-100"
                                >
                                    <div>
                                        <p className="text-sm font-bold uppercase tracking-[0.12em] text-red-700">Expires Today</p>
                                        <p className="mt-1 text-sm text-red-900">Immediate staff follow-up required</p>
                                    </div>
                                    <span className="text-3xl font-bold text-red-700">{expiryHighlights.todayExpiring.length}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => history.push("/planValidity?expiry=week")}
                                    className="flex items-center justify-between rounded-2xl border border-amber-100 bg-amber-50 px-4 py-4 text-left transition hover:border-amber-200 hover:bg-amber-100"
                                >
                                    <div>
                                        <p className="text-sm font-bold uppercase tracking-[0.12em] text-amber-700">This Week</p>
                                        <p className="mt-1 text-sm text-amber-900">Good time to renew before expiry</p>
                                    </div>
                                    <span className="text-3xl font-bold text-amber-700">{expiryHighlights.weekExpiring.length}</span>
                                </button>
                            </div>
                        </motion.div>
                    </div>

                    {/* ── Recently Registered ── */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.26 }}
                        className="rounded-xl border border-borderColor bg-white p-6 shadow-sm"
                    >
                        <div className="mb-5 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50">
                                    <HiOutlineUserPlus className="h-5 w-5 text-emerald-600" />
                                </div>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-600">New Registrations</p>
                                    <h3 className="text-xl font-semibold text-tColor">Recently Registered Members</h3>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="w-fit rounded-full border border-borderColor bg-white px-4 py-2 text-xs font-bold text-tColor transition hover:border-emerald-500 hover:text-emerald-600"
                                onClick={() => history.push("/user")}
                            >
                                View All Members
                            </button>
                        </div>
                        <div className="grid gap-4 md:grid-cols-3">
                            <button
                                type="button"
                                onClick={() => history.push("/user")}
                                className="flex items-center justify-between rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-4 text-left transition hover:border-emerald-300 hover:bg-emerald-100"
                            >
                                <div>
                                    <p className="text-sm font-bold uppercase tracking-[0.12em] text-emerald-700">Today</p>
                                    <p className="mt-1 text-xs text-emerald-900">Registered today</p>
                                </div>
                                <span className="text-4xl font-bold text-emerald-700">{recentRegistrations.today}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => history.push("/user")}
                                className="flex items-center justify-between rounded-2xl border border-blue-100 bg-blue-50 px-5 py-4 text-left transition hover:border-blue-300 hover:bg-blue-100"
                            >
                                <div>
                                    <p className="text-sm font-bold uppercase tracking-[0.12em] text-blue-700">This Week</p>
                                    <p className="mt-1 text-xs text-blue-900">Last 7 days</p>
                                </div>
                                <span className="text-4xl font-bold text-blue-700">{recentRegistrations.week}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => history.push("/user")}
                                className="flex items-center justify-between rounded-2xl border border-violet-100 bg-violet-50 px-5 py-4 text-left transition hover:border-violet-300 hover:bg-violet-100"
                            >
                                <div>
                                    <p className="text-sm font-bold uppercase tracking-[0.12em] text-violet-700">This Month</p>
                                    <p className="mt-1 text-xs text-violet-900">Last 30 days</p>
                                </div>
                                <span className="text-4xl font-bold text-violet-700">{recentRegistrations.month}</span>
                            </button>
                        </div>
                    </motion.div>

                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.28 }}
                            className="rounded-xl border border-borderColor bg-white p-6 shadow-sm xl:col-span-7"
                        >
                            <div className="mb-6">
                                <h3 className="mb-1 text-xl font-semibold text-tColor">Today&apos;s Profile Pipeline</h3>
                                <p className="text-sm text-tpColor">Real-time overview of profile submissions and approval status</p>
                            </div>
                            <DoughnutChart data={dashDetails} compact />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.36 }}
                            className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm xl:col-span-5"
                        >
                            <h3 className="mb-4 text-lg font-semibold text-slate-900">Quick Stats</h3>
                            <div className="grid grid-cols-3 gap-3">
                                {secondaryStats.map((stat) => {
                                    const Icon = stat.icon
                                    return (
                                        <button
                                            key={stat.id}
                                            type="button"
                                            onClick={() => stat.path && history.push(stat.path)}
                                            className="flex flex-col items-center gap-2 rounded-xl p-3 text-center transition hover:scale-105 hover:shadow-md"
                                            style={{ backgroundColor: stat.bg }}
                                        >
                                            <div className="flex h-9 w-9 items-center justify-center rounded-full" style={{ backgroundColor: stat.color + "22" }}>
                                                <Icon className="h-4 w-4" style={{ color: stat.color }} />
                                            </div>
                                            <span className="text-xl font-bold" style={{ color: stat.color }}>{stat.value}</span>
                                            <span className="text-xs font-medium leading-tight text-slate-600">{stat.label}</span>
                                        </button>
                                    )
                                })}
                            </div>
                        </motion.div>
                    </div>

                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.42 }}
                            className="rounded-xl border border-borderColor bg-white p-6 shadow-sm xl:col-span-4"
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-primeColor">Revenue Report</p>
                                    <h3 className="mt-1 text-xl font-semibold text-tColor">Active subscription revenue</h3>
                                </div>
                                <button
                                    type="button"
                                    className="rounded-full border border-borderColor bg-white px-3 py-2 text-xs font-bold text-tColor transition hover:border-primeColor hover:text-primeColor"
                                    onClick={() => history.push("/planValidity")}
                                >
                                    View List
                                </button>
                            </div>
                            <button
                                type="button"
                                className="mt-6 flex w-full flex-col rounded-2xl bg-primeSoft p-5 text-left transition hover:bg-primeColor hover:text-white"
                                onClick={() => history.push("/planValidity")}
                            >
                                <span className="text-sm font-semibold">Total Revenue</span>
                                <span className="mt-2 text-4xl font-bold">Rs. {Number(revenueSummary).toLocaleString()}</span>
                                <span className="mt-3 text-sm opacity-80">{activeSubscriptions.length} active subscriptions contributing right now</span>
                            </button>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.48 }}
                            className="rounded-xl border border-borderColor bg-white p-6 shadow-sm xl:col-span-8"
                        >
                            <div className="mb-5">
                                <p className="text-xs font-bold uppercase tracking-[0.18em] text-primeColor">Plan-wise User Report</p>
                                <h3 className="mt-1 text-xl font-semibold text-tColor">Subscriptions by plan</h3>
                            </div>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                                {planWiseReport.length > 0 ? planWiseReport.map((plan) => (
                                    <button
                                        key={plan.planId}
                                        type="button"
                                        className="rounded-2xl border border-borderColor bg-lightGrey/35 p-4 text-left transition hover:-translate-y-1 hover:border-primeColor hover:bg-primeSoft"
                                        onClick={() => history.push(`/planValidity?planId=${plan.planId}`)}
                                    >
                                        <p className="text-sm font-bold uppercase tracking-[0.12em] text-tpColor">{plan.planName}</p>
                                        <p className="mt-3 text-3xl font-bold text-tColor">{plan.users}</p>
                                        <p className="mt-2 text-sm text-tpColor">Revenue: Rs. {Number(plan.revenue).toLocaleString()}</p>
                                    </button>
                                )) : (
                                    <div className="rounded-2xl border border-dashed border-borderColor bg-lightGrey/20 p-5 text-sm text-tpColor md:col-span-2 xl:col-span-3">
                                        No active subscriptions found yet.
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </div>

                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.56 }}
                            className="rounded-xl border border-borderColor bg-white p-6 shadow-sm xl:col-span-12"
                        >
                            <div className="mb-6">
                                <h3 className="mb-1 text-xl font-semibold text-tColor">Monthly Activity Overview</h3>
                                <p className="text-sm text-tpColor">Track approvals, registrations, and successful matches over time</p>
                            </div>
                            <div className="h-[300px]">
                                <Bar data={activityData} options={activityOptions} />
                            </div>
                        </motion.div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.62 }}
                        className="rounded-xl border border-borderColor bg-white p-6 shadow-sm"
                    >
                        <h3 className="mb-4 text-xl font-semibold text-tColor">Recent Activity</h3>
                        <div className="space-y-3">
                            {[
                                { id: "act1", action: "New registration", user: "Priya Sharma", time: "5 min ago", type: "success" },
                                { id: "act2", action: "Profile approved", user: "Rahul Kumar", time: "12 min ago", type: "info" },
                                { id: "act3", action: "Plan upgraded", user: "Anita Patel", time: "1 hour ago", type: "success" },
                                { id: "act4", action: "Enquiry submitted", user: "Vikram Singh", time: "2 hours ago", type: "warning" },
                            ].map((activity) => (
                                <div key={activity.id} className="flex items-center justify-between rounded-lg bg-lightGrey/60 p-3 transition-colors hover:bg-lightGrey">
                                    <div className="flex items-center gap-3">
                                        <div className={`h-2 w-2 rounded-full ${activity.type === "success" ? "bg-primeColor" : activity.type === "info" ? "bg-accent" : "bg-primeDark"}`} />
                                        <div>
                                            <p className="text-sm font-medium text-tColor">{activity.action}</p>
                                            <p className="text-xs text-tpColor">{activity.user}</p>
                                        </div>
                                    </div>
                                    <span className="text-xs text-tpColor">{activity.time}</span>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </>
            )}
        </motion.div>
    )
}

export default Dashboard
